import React from 'react';

import { useIntl } from 'react-intl';

import { TimePicker } from 'antd';
import moment, { type Moment } from 'moment';

import { DAYS_OF_WEEK } from '@src/constants/commons';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type { WorkJourneyShiftDayConfig } from '@src/packages/workJourney/types';

import { ResponsiveTable } from '@src/components/Table';

import styles from '../../../styles.module.less';

interface CollationWeekRow {
  key: string;
  weekLabel: string;
  weekIndex: number;
  [day: string]: unknown;
}

const CollationCycleSelector = () => {
  const { workJourneyForm, setWorkJourneyForm } = useWorkJourneyContext();
  const intl = useIntl();

  const shift = workJourneyForm?.workShift;
  const weeks = shift?.recurrence?.weeks || [];

  const dataSource: CollationWeekRow[] = weeks
    .map((week, weekIndex) => {
      const weekData: CollationWeekRow = {
        key: `week-${weekIndex + 1}`,
        weekLabel: intl.formatMessage({ id: 'shifts.week.number' }, { number: weekIndex + 1 }),
        weekIndex,
      };

      DAYS_OF_WEEK.forEach(day => {
        if (week.days && week.days[day.value]) {
          weekData[day.value] = week.days[day.value];
        }
      });

      return weekData;
    })
    .filter(weekData => {
      const configuredDays = DAYS_OF_WEEK.filter(
        day => weekData[day.value] && Object.keys(weekData[day.value] as object).length > 0,
      );
      return configuredDays.length > 0;
    });

  const getBreakStartTime = (weekIndex: number, day: string | number) => {
    const weekData = weeks[weekIndex];
    if (!weekData?.days?.[day]) return null;

    const dayConfig = weekData.days[day] as WorkJourneyShiftDayConfig;
    const breakTime = dayConfig.startBreakTime;
    return breakTime ? moment(breakTime, 'HH:mm') : null;
  };

  const isValidMomentTime = (time: Moment | null) => {
    return time && moment.isMoment(time) && time.isValid();
  };

  const updateBreakStartTime = (weekIndex: number, day: string | number, newTime: Moment | null) => {
    setWorkJourneyForm(prevForm => {
      const newForm = { ...prevForm };
      if (!newForm.workShift?.recurrence?.weeks) return newForm;

      newForm.workShift = { ...newForm.workShift };
      newForm.workShift.recurrence = { ...newForm.workShift.recurrence };
      newForm.workShift.recurrence.weeks = [...newForm.workShift.recurrence.weeks];

      const existingWeek = newForm.workShift.recurrence.weeks[weekIndex];
      const existingDay = (existingWeek.days?.[day] ?? {}) as WorkJourneyShiftDayConfig;

      newForm.workShift.recurrence.weeks[weekIndex] = {
        ...existingWeek,
      };
      newForm.workShift.recurrence.weeks[weekIndex].days = {
        ...existingWeek.days,
      };
      newForm.workShift.recurrence.weeks[weekIndex].days![day] = {
        ...existingDay,
        startBreakTime: isValidMomentTime(newTime) ? newTime.format('HH:mm') : null,
      };

      return newForm;
    });
  };

  const renderTimePicker = (weekIndex: number, day: string | number) => {
    const currentTime = getBreakStartTime(weekIndex, day);

    return (
      <TimePicker
        format="HH:mm"
        style={{ width: '100%' }}
        placeholder={intl.formatMessage({ id: 'workJourney.withoutBreak' })}
        value={currentTime}
        onChange={newTime => updateBreakStartTime(weekIndex, day, newTime)}
        allowClear
      />
    );
  };

  const columns = [
    {
      key: 'week',
      title: intl.formatMessage({ id: 'shifts.weeks' }),
      dataIndex: 'weekLabel',
      align: 'center' as const,
      fixed: 'left' as const,
    },
    ...DAYS_OF_WEEK.map(day => ({
      key: day.value,
      title: day.label,
      dataIndex: day.value,
      align: 'center' as const,
      render: (record: CollationWeekRow) => {
        return renderTimePicker(record.weekIndex as number, day.value);
      },
    })),
  ];

  return (
    <div className={styles.collationCycleSelector}>
      {workJourneyForm.workShift ? (
        <ResponsiveTable dataSource={dataSource} columns={columns} scroll={{ x: 'max-content' }} />
      ) : (
        <></>
      )}
    </div>
  );
};

export default React.memo(CollationCycleSelector);
